import {
  GeneralMessageRecipientPreviewItem,
  GeneralMessageRecipientSelectionGroup,
  GeneralMessageRecipientSelectionItem,
  GeneralMessageRecipientSelection,
  GeneralMessageRecipientType,
  GeneralMessageRecipientsFilter
} from 'Src/helpers/service/admin/generalMessage';

export const GENERAL_MESSAGE_RECIPIENT_SELECTION_MODE = {
  MANUAL: 'MANUAL' as 'MANUAL',
  FILTER: 'FILTER' as 'FILTER'
};

export interface GeneralMessageRecipientSelectionStateGroup extends GeneralMessageRecipientSelectionGroup {
  matchingCount?: number;
  previewItems?: GeneralMessageRecipientPreviewItem[];
}

function getGeneralMessageRecipientSelectionFilters(
  group: GeneralMessageRecipientSelectionGroup
): GeneralMessageRecipientsFilter[] {
  const filters = Array.isArray(group.filters) ? group.filters : [];
  if (filters.length > 0) return filters;
  return group.mode === GENERAL_MESSAGE_RECIPIENT_SELECTION_MODE.FILTER && group.filter ? [group.filter] : [];
}

function getGeneralMessageRecipientFilterKey(filter: GeneralMessageRecipientsFilter): string {
  return JSON.stringify({ where: filter.where || {}, order: filter.order || '' });
}

function isGeneralMessageRecipientFilterUnrestricted(filter: GeneralMessageRecipientsFilter): boolean {
  const where = filter && filter.where;
  return !where || Object.keys(where).length === 0;
}

export interface GeneralMessageRecipientSelectionState {
  students: GeneralMessageRecipientSelectionStateGroup;
  staffs: GeneralMessageRecipientSelectionStateGroup;
  governors: GeneralMessageRecipientSelectionStateGroup;
}

export function getEmptyGeneralMessageRecipientSelectionGroup(): GeneralMessageRecipientSelectionStateGroup {
  return {
    mode: GENERAL_MESSAGE_RECIPIENT_SELECTION_MODE.MANUAL,
    all: false,
    selected: [],
    excluded: [],
    filter: {},
    matchingCount: 0,
    previewItems: []
  };
}

export function getEmptyGeneralMessageRecipientSelectionState(): GeneralMessageRecipientSelectionState {
  return {
    students: getEmptyGeneralMessageRecipientSelectionGroup(),
    staffs: getEmptyGeneralMessageRecipientSelectionGroup(),
    governors: getEmptyGeneralMessageRecipientSelectionGroup()
  };
}

export function getGeneralMessageRecipientSelectionItemKey(item: GeneralMessageRecipientSelectionItem): string {
  return `${item.userId}_${item.permissionId}`;
}

export function getGeneralMessageRecipientPreviewItemKey(item: GeneralMessageRecipientPreviewItem): string {
  return `${item.userId}_${item.permissionId}`;
}

export function getGeneralMessageRecipientSelectionItemFromPreview(
  item: GeneralMessageRecipientPreviewItem
): GeneralMessageRecipientSelectionItem {
  return {
    userId: item.userId,
    permissionId: item.permissionId
  };
}

export function isGeneralMessageRecipientSelectionItemEqual(
  firstItem: GeneralMessageRecipientSelectionItem,
  secondItem: GeneralMessageRecipientSelectionItem
): boolean {
  return firstItem.userId === secondItem.userId && firstItem.permissionId === secondItem.permissionId;
}

export function isGeneralMessageRecipientSelectionItemInList(
  item: GeneralMessageRecipientSelectionItem,
  list: GeneralMessageRecipientSelectionItem[] = []
): boolean {
  return list.some(listItem => isGeneralMessageRecipientSelectionItemEqual(item, listItem));
}

function normalizeRecipientFilterValue(value: any): string {
  return String(typeof value === 'undefined' || value === null ? '' : value).toLocaleLowerCase();
}

function getRecipientPreviewFieldValues(item: GeneralMessageRecipientPreviewItem, field: string): any[] {
  switch (field) {
    case 'forms':
    case 'formId':
    case 'formIds':
    case 'permissionFormIds':
    case 'permissions.formIds':
      return [...(item.formIds || []), item.formId].filter(Boolean);
    case 'houses':
    case 'studentHouseName':
    case 'houseId':
    case 'houseIds':
    case 'permissionHouseIds':
    case 'permissions.houseIds':
      return [...(item.houseIds || []), item.houseId].filter(Boolean);
    case 'sports':
    case 'sportId':
    case 'sportIds':
    case 'permissionSportIds':
    case 'permissions.sportIds':
      return item.hasAllSportsAccess ? ['*'] : item.sportIds || [];
    case 'ages':
    case 'yearGroup':
      return typeof item.yearGroup === 'undefined' ? [] : [item.yearGroup];
    case 'role':
    case 'preset':
    case 'presets':
    case 'permissions.preset':
      return item.presets || (item.preset ? [item.preset] : []);
    case 'tag':
    case 'tags':
    case 'tagIds':
    case 'permissions.tagIds':
      return item.tagIds || [];
    default:
      return [(item as any)[field]];
  }
}

function isRecipientPreviewValueMatched(values: any[], condition: any): boolean {
  const definedValues = values.filter(value => typeof value !== 'undefined' && value !== null);

  if (condition && typeof condition === 'object' && !Array.isArray(condition)) {
    const like = typeof condition.like !== 'undefined' ? condition.like : condition.$regex;
    if (typeof like !== 'undefined') {
      const expected = normalizeRecipientFilterValue(like);
      return definedValues.some(value => normalizeRecipientFilterValue(value).indexOf(expected) !== -1);
    }

    const included = condition.$in || condition.inq;
    if (Array.isArray(included)) {
      const expected = included.map(value => String(value));
      return condition.$all
        ? expected.every(value => definedValues.some(itemValue => String(itemValue) === value))
        : definedValues.some(value => expected.indexOf(String(value)) !== -1);
    }

    if (Array.isArray(condition.$nin)) {
      const excluded = condition.$nin.map(value => String(value));
      return definedValues.every(value => excluded.indexOf(String(value)) === -1);
    }

    if (typeof condition.$ne !== 'undefined') {
      return definedValues.every(value => String(value) !== String(condition.$ne));
    }

    if (typeof condition.$exists === 'boolean') {
      return condition.$exists ? definedValues.length > 0 : definedValues.length === 0;
    }

    const from = condition.from || condition.$gte || condition.$gt;
    const to = condition.to || condition.$lte || condition.$lt;
    if (typeof from !== 'undefined' || typeof to !== 'undefined') {
      return definedValues.some(value => {
        const timestamp = new Date(value).getTime();
        if (Number.isNaN(timestamp)) return false;
        const fromTime = typeof from === 'undefined' || from === '' ? undefined : new Date(from).getTime();
        const toTime = typeof to === 'undefined' || to === '' ? undefined : new Date(to).getTime();
        return (
          (typeof fromTime === 'undefined' || timestamp >= fromTime) &&
          (typeof toTime === 'undefined' || timestamp <= toTime)
        );
      });
    }
  }

  if (Array.isArray(condition)) {
    const expected = condition.map(value => String(value));
    return definedValues.some(value => expected.indexOf(String(value)) !== -1);
  }

  return definedValues.some(value => String(value) === String(condition));
}

function isGeneralMessageRecipientPreviewItemMatchedByWhere(
  item: GeneralMessageRecipientPreviewItem,
  where: any
): boolean {
  if (!where || Object.keys(where).length === 0) return true;

  return Object.keys(where).every(field => {
    const condition = where[field];
    if (field === '$or') {
      return (
        Array.isArray(condition) &&
        condition.some(entry => isGeneralMessageRecipientPreviewItemMatchedByWhere(item, entry))
      );
    }
    if (field === '$and') {
      return (
        Array.isArray(condition) &&
        condition.every(entry => isGeneralMessageRecipientPreviewItemMatchedByWhere(item, entry))
      );
    }
    return isRecipientPreviewValueMatched(getRecipientPreviewFieldValues(item, field), condition);
  });
}

export function addGeneralMessageRecipientSelectionItems(
  currentItems: GeneralMessageRecipientSelectionItem[] = [],
  nextItems: GeneralMessageRecipientSelectionItem[] = []
): GeneralMessageRecipientSelectionItem[] {
  return nextItems.reduce(
    (result, item) => {
      return isGeneralMessageRecipientSelectionItemInList(item, result) ? result : [...result, item];
    },
    [...currentItems]
  );
}

export function removeGeneralMessageRecipientSelectionItems(
  currentItems: GeneralMessageRecipientSelectionItem[] = [],
  itemsToRemove: GeneralMessageRecipientSelectionItem[] = []
): GeneralMessageRecipientSelectionItem[] {
  return currentItems.filter(item => !isGeneralMessageRecipientSelectionItemInList(item, itemsToRemove));
}

export function toggleGeneralMessageRecipientSelectionItem(
  currentItems: GeneralMessageRecipientSelectionItem[] = [],
  item: GeneralMessageRecipientSelectionItem
): GeneralMessageRecipientSelectionItem[] {
  return isGeneralMessageRecipientSelectionItemInList(item, currentItems)
    ? removeGeneralMessageRecipientSelectionItems(currentItems, [item])
    : addGeneralMessageRecipientSelectionItems(currentItems, [item]);
}

export function isGeneralMessageRecipientPreviewItemSelected(
  item: GeneralMessageRecipientPreviewItem,
  group: GeneralMessageRecipientSelectionGroup
): boolean {
  const selectionItem = getGeneralMessageRecipientSelectionItemFromPreview(item);
  const selected = group.selected || [];
  const excluded = group.excluded || [];

  if (isGeneralMessageRecipientSelectionItemInList(selectionItem, excluded)) {
    return false;
  }

  if (isGeneralMessageRecipientSelectionItemInList(selectionItem, selected)) {
    return true;
  }

  if (group.mode === GENERAL_MESSAGE_RECIPIENT_SELECTION_MODE.FILTER) {
    return getGeneralMessageRecipientSelectionFilters(group).some(filter =>
      isGeneralMessageRecipientPreviewItemMatchedByWhere(item, (filter || {}).where || {})
    );
  }

  return false;
}

export function selectGeneralMessageRecipientPageItems(
  group: GeneralMessageRecipientSelectionStateGroup,
  pageItems: GeneralMessageRecipientPreviewItem[]
): GeneralMessageRecipientSelectionStateGroup {
  const selectionItems = pageItems.map(getGeneralMessageRecipientSelectionItemFromPreview);

  if (group.mode === GENERAL_MESSAGE_RECIPIENT_SELECTION_MODE.FILTER) {
    const selectionFilters = getGeneralMessageRecipientSelectionFilters(group);
    const explicitlySelectedItems = pageItems
      .filter(
        item =>
          !selectionFilters.some(filter =>
            isGeneralMessageRecipientPreviewItemMatchedByWhere(item, (filter || {}).where || {})
          )
      )
      .map(getGeneralMessageRecipientSelectionItemFromPreview);

    return {
      ...group,
      // Items covered by the saved filter only need to be removed from its
      // exclusion list. Items from a later, different search must be kept as
      // explicit selections so Select this page can extend a FILTER group.
      selected: addGeneralMessageRecipientSelectionItems(group.selected || [], explicitlySelectedItems),
      excluded: removeGeneralMessageRecipientSelectionItems(group.excluded || [], selectionItems)
    };
  }

  return {
    ...group,
    selected: addGeneralMessageRecipientSelectionItems(group.selected || [], selectionItems)
  };
}

export function unselectGeneralMessageRecipientPageItems(
  group: GeneralMessageRecipientSelectionStateGroup,
  pageItems: GeneralMessageRecipientPreviewItem[]
): GeneralMessageRecipientSelectionStateGroup {
  const selectionItems = pageItems.map(getGeneralMessageRecipientSelectionItemFromPreview);

  if (group.mode === GENERAL_MESSAGE_RECIPIENT_SELECTION_MODE.FILTER) {
    const selectionFilters = getGeneralMessageRecipientSelectionFilters(group);
    const filterSelectedItems = pageItems
      .filter(item =>
        selectionFilters.some(filter =>
          isGeneralMessageRecipientPreviewItemMatchedByWhere(item, (filter || {}).where || {})
        )
      )
      .map(getGeneralMessageRecipientSelectionItemFromPreview);

    return {
      ...group,
      // Removing a page has two meanings in FILTER mode: exclude records that
      // belong to the saved filter and remove explicit records added by later
      // searches. Keeping these paths separate prevents stale selections.
      selected: removeGeneralMessageRecipientSelectionItems(group.selected || [], selectionItems),
      excluded: addGeneralMessageRecipientSelectionItems(group.excluded || [], filterSelectedItems)
    };
  }

  return {
    ...group,
    selected: removeGeneralMessageRecipientSelectionItems(group.selected || [], selectionItems)
  };
}

export function selectAllFilteredGeneralMessageRecipients(
  group: GeneralMessageRecipientSelectionStateGroup,
  filter: GeneralMessageRecipientsFilter,
  matchingCount: number
): GeneralMessageRecipientSelectionStateGroup {
  const existingFilters = getGeneralMessageRecipientSelectionFilters(group);
  const filterKey = getGeneralMessageRecipientFilterKey(filter);
  const filters = existingFilters.some(
    existingFilter => getGeneralMessageRecipientFilterKey(existingFilter) === filterKey
  )
    ? existingFilters
    : [...existingFilters, filter];

  return {
    ...group,
    mode: GENERAL_MESSAGE_RECIPIENT_SELECTION_MODE.FILTER,
    all: group.all === true || isGeneralMessageRecipientFilterUnrestricted(filter),
    filter,
    filters,
    // FILTER mode is a union of all saved queries and explicit recipients.
    // The backend resolves and deduplicates the union before delivery.
    selected: group.selected || [],
    excluded: group.excluded || [],
    matchingCount: (group.matchingCount || 0) + (filters.length > existingFilters.length ? matchingCount : 0)
  };
}

export function clearGeneralMessageRecipientSelectionGroup(): GeneralMessageRecipientSelectionStateGroup {
  return getEmptyGeneralMessageRecipientSelectionGroup();
}

export function getGeneralMessageRecipientSelectionGroupSize(
  group: GeneralMessageRecipientSelectionStateGroup
): number {
  const selected = group.selected || [];
  const excluded = group.excluded || [];

  if (group.mode === GENERAL_MESSAGE_RECIPIENT_SELECTION_MODE.FILTER) {
    const matchingCount = group.matchingCount || 0;
    return Math.max(matchingCount - excluded.length, 0);
  }

  return selected.length;
}

export function isGeneralMessageRecipientSelectionGroupCompleted(
  group: GeneralMessageRecipientSelectionStateGroup
): boolean {
  return getGeneralMessageRecipientSelectionGroupSize(group) > 0;
}

export function getGeneralMessageRecipientSelectionForSave(
  selectionState: GeneralMessageRecipientSelectionState
): GeneralMessageRecipientSelection {
  return {
    students: {
      mode: selectionState.students.mode,
      all: selectionState.students.all === true,
      selected: selectionState.students.selected || [],
      excluded: selectionState.students.excluded || [],
      filter: selectionState.students.filter || {},
      filters: getGeneralMessageRecipientSelectionFilters(selectionState.students)
    },
    staffs: {
      mode: selectionState.staffs.mode,
      all: selectionState.staffs.all === true,
      selected: selectionState.staffs.selected || [],
      excluded: selectionState.staffs.excluded || [],
      filter: selectionState.staffs.filter || {},
      filters: getGeneralMessageRecipientSelectionFilters(selectionState.staffs)
    },
    governors: {
      mode: selectionState.governors.mode,
      all: selectionState.governors.all === true,
      selected: selectionState.governors.selected || [],
      excluded: selectionState.governors.excluded || [],
      filter: selectionState.governors.filter || {},
      filters: getGeneralMessageRecipientSelectionFilters(selectionState.governors)
    }
  };
}

export function updateGeneralMessageRecipientSelectionStateGroup(
  selectionState: GeneralMessageRecipientSelectionState,
  recipientType: GeneralMessageRecipientType,
  group: GeneralMessageRecipientSelectionStateGroup
): GeneralMessageRecipientSelectionState {
  return {
    ...selectionState,
    [recipientType]: group
  };
}
